import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import type {
  DashboardStructure,
  DashboardTab,
  MarketApp,
  MarketApps,
  TabEntry
} from '@/features/Dashboard/types';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

// DRY helpers for Dashboard feature. These are pure and framework-agnostic.

/**
 * Returns a safe, effective tab name with a sensible default.
 */
export const getEffectiveTabName = (tabName?: string): string => tabName || TAB_DEFAULT_BASE_NAME;

/**
 * Ensures all MarketApps have unique app and tab IDs and normalizes tiles.
 * This function is idempotent for already-correct data.
 */
export const processMarketApps = (marketApps: MarketApps): MarketApp[] => {
  const usedAppIds = new Set<string>();
  const usedTabIds = new Set<string>();

  return marketApps.map(app => {
    // Widget-like entry (already normalized, passthrough)
    if ('type' in app) {
      return {
        ...app,
        tiles: app.tiles.map(tile => ({
          ...tile
        }))
      };
    }

    let appId = app.id;
    if (!appId || (appId && usedAppIds.has(appId))) {
      appId = generateUniqueUUID(usedAppIds);
    } else {
      usedAppIds.add(appId);
    }

    const tabs = app.tabs.map(tab => {
      const existing = tab.id;
      let tabId = existing ?? '';
      if (!tabId || usedTabIds.has(tabId)) {
        tabId = generateUniqueUUID(usedTabIds);
      } else {
        usedTabIds.add(tabId);
      }
      return {
        ...tab,
        id: tabId
      };
    });

    return {
      ...app,
      id: appId,
      tabs
    };
  });
};

/**
 * Checks if a tab value is in the new format (has id and content)
 */
export const isTabEntry = (value: unknown): value is TabEntry => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'content' in value &&
    Array.isArray((value as { content: unknown }).content)
  );
};

/**
 * Checks if a tab value is in legacy format (just an array)
 */
export const isLegacyTab = (value: unknown): value is MarketApps => {
  return Array.isArray(value);
};

/**
 * Normalizes a tab entry to the new format with id.
 * If it's already in new format, returns as-is.
 * If it's legacy format, generates an id dynamically.
 */
export const normalizeTabEntry = (tabName: string, value: MarketApps | TabEntry): TabEntry => {
  if (isTabEntry(value)) {
    return value;
  }
  // Legacy format - generate id dynamically
  return {
    id: generateUniqueUUID(new Set()),
    content: value
  };
};

/**
 * Gets MarketApps from a tab entry (handles both legacy and new formats)
 */
export const getTabContent = (value: MarketApps | TabEntry): MarketApps => {
  if (isTabEntry(value)) {
    return value.content;
  }
  return value;
};

const isTabCollection = (value: unknown): value is DashboardTab => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value).every(v => Array.isArray(v) || isTabEntry(v));
};

const extractRawTabs = (
  value: unknown,
  originalName: string,
  effectiveName: string
): DashboardTab | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;

  if (Array.isArray(record.content)) {
    return { Main: record.content } as DashboardTab;
  }

  if (isTabCollection(value)) {
    return value;
  }

  const innerByExpected = record[effectiveName];
  if (innerByExpected && typeof innerByExpected === 'object') {
    return innerByExpected as DashboardTab;
  }

  const innerByOriginal = record[originalName];
  if (innerByOriginal && typeof innerByOriginal === 'object') {
    return innerByOriginal as DashboardTab;
  }

  const firstKey = Object.keys(record)[0];
  const firstVal = firstKey ? record[firstKey] : undefined;
  if (firstVal && typeof firstVal === 'object' && isTabCollection(firstVal)) {
    return firstVal;
  }

  return undefined;
};

const normalizeTabs = (rawTabs: DashboardTab): DashboardTab => {
  const normalizedTabs: DashboardTab = {};
  Object.entries(rawTabs).forEach(([tabName, tabValue]) => {
    normalizedTabs[tabName] = normalizeTabEntry(tabName, tabValue);
  });
  return normalizedTabs;
};

/**
 * Normalizes various server payload shapes to a DashboardTab structure.
 * Accepts several legacy formats and returns a consistent representation.
 * Legacy tabs are normalized to include dynamically generated IDs.
 */
export const normalizeDashboardTabs = (
  value: unknown,
  originalName: string,
  effectiveName: string
): DashboardTab | undefined => {
  const rawTabs = extractRawTabs(value, originalName, effectiveName);
  if (!rawTabs) return undefined;
  return normalizeTabs(rawTabs);
};

/**
 * Builds a server payload from either MarketApps array or a structure-like object.
 * Encapsulates legacy allowances and fallbacks.
 * Returns tabs in new format with id.
 */
export const buildDashboardPayload = (
  input: MarketApps | DashboardStructure,
  dashboardName: string,
  tabName?: string
): DashboardTab => {
  if (Array.isArray(input)) {
    const tab = getEffectiveTabName(tabName);
    return {
      [tab]: {
        id: generateUniqueUUID(new Set()),
        content: input
      }
    } as DashboardTab;
  }

  const inputRecord = input as Record<string, unknown>;
  if (inputRecord && 'content' in inputRecord && Array.isArray(inputRecord.content)) {
    const tab = getEffectiveTabName(tabName);
    return {
      [tab]: {
        id: generateUniqueUUID(new Set()),
        content: inputRecord.content as MarketApps
      }
    } as DashboardTab;
  }

  if (inputRecord && typeof inputRecord === 'object') {
    let tabs: DashboardTab;
    if (inputRecord[dashboardName] && typeof inputRecord[dashboardName] === 'object') {
      tabs = inputRecord[dashboardName] as DashboardTab;
    } else {
      tabs = inputRecord as DashboardTab;
    }

    // Normalize all tabs to new format
    const normalizedTabs: DashboardTab = {};
    Object.entries(tabs).forEach(([name, value]) => {
      normalizedTabs[name] = normalizeTabEntry(name, value);
    });
    return normalizedTabs;
  }

  const tab = getEffectiveTabName(tabName);
  return {
    [tab]: {
      id: generateUniqueUUID(new Set()),
      content: []
    }
  } as DashboardTab;
};
